import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import ClientAvatar from '../../../components/ClientAvatar';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ClientTable = ({ 
  clients, 
  selectedClients, 
  onSelectClient, 
  onSelectAll, 
  onEditClient, 
  onMessageClient, 
  onAddNote,
  sortConfig,
  onSort 
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpansion = (clientId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded?.has(clientId)) {
      newExpanded?.delete(clientId);
    } else {
      newExpanded?.add(clientId);
    }
    setExpandedRows(newExpanded);
  };

  const getLifecycleStageColor = (stage) => {
    const colors = {
      'Lead': 'bg-warning/10 text-warning border-warning/20',
      'Active Buyer': 'bg-primary/10 text-primary border-primary/20',
      'Under Contract': 'bg-accent/10 text-accent border-accent/20',
      'Closed': 'bg-success/10 text-success border-success/20',
      'Past Client': 'bg-secondary/10 text-secondary border-secondary/20'
    };
    return colors?.[stage] || 'bg-muted/10 text-muted-foreground border-muted/20';
  };

  const getEngagementColor = (level) => {
    const colors = {
      'High': 'text-success',
      'Medium': 'text-warning',
      'Low': 'text-error'
    };
    return colors?.[level] || 'text-muted-foreground';
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="w-12 p-4">
                  <Checkbox
                    checked={selectedClients?.length === clients?.length && clients?.length > 0}
                    onChange={onSelectAll}
                    indeterminate={selectedClients?.length > 0 && selectedClients?.length < clients?.length}
                  />
                </th>
                <th className="text-left p-4 font-medium text-foreground">
                  <button
                    onClick={() => onSort('name')}
                    className="flex items-center space-x-2 hover:text-primary transition-smooth"
                  >
                    <span>Client</span>
                    <Icon name={getSortIcon('name')} size={16} />
                  </button>
                </th>
                <th className="text-left p-4 font-medium text-foreground">
                  <button
                    onClick={() => onSort('phone')}
                    className="flex items-center space-x-2 hover:text-primary transition-smooth"
                  >
                    <span>Phone</span>
                    <Icon name={getSortIcon('phone')} size={16} />
                  </button>
                </th>
                <th className="text-left p-4 font-medium text-foreground">
                  <button
                    onClick={() => onSort('lifecycle_stage')}
                    className="flex items-center space-x-2 hover:text-primary transition-smooth"
                  >
                    <span>Stage</span>
                    <Icon name={getSortIcon('lifecycle_stage')} size={16} />
                  </button>
                </th>
                <th className="text-left p-4 font-medium text-foreground">
                  <button
                    onClick={() => onSort('last_interaction')}
                    className="flex items-center space-x-2 hover:text-primary transition-smooth"
                  >
                    <span>Last Contact</span>
                    <Icon name={getSortIcon('last_interaction')} size={16} />
                  </button>
                </th>
                <th className="text-left p-4 font-medium text-foreground">Engagement</th>
                <th className="text-right p-4 font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients?.map((client) => (
                <tr key={client?.id} className="border-b border-border hover:bg-muted/30 transition-smooth">
                  <td className="p-4">
                    <Checkbox
                      checked={selectedClients?.includes(client?.id)}
                      onChange={() => onSelectClient(client?.id)}
                    />
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <ClientAvatar
                        src={client?.avatar_url || client?.avatar}
                        name={client?.name}
                        size={40}
                      />
                      <div>
                        <p className="font-medium text-foreground">{client?.name}</p>
                        <p className="text-sm text-muted-foreground">{client?.location}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm text-foreground">{client?.phone}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getLifecycleStageColor(client?.lifecycle_stage)}`}>
                      {client?.lifecycle_stage || 'N/A'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm text-foreground">{client?.last_interaction ? new Date(client.last_interaction).toLocaleDateString() : 'Never'}</p>
                      <p className="text-xs text-muted-foreground">{client?.last_interaction_type || ''}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${getEngagementColor(client?.engagement_level) === 'text-success' ? 'bg-success' : getEngagementColor(client?.engagement_level) === 'text-warning' ? 'bg-warning' : 'bg-error'}`} />
                      <span className={`text-sm font-medium ${getEngagementColor(client?.engagement_level)}`}>
                        {client?.engagement_level || 'N/A'}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onMessageClient(client)}
                        className="h-8 w-8"
                      >
                        <Icon name="MessageSquare" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onAddNote(client)}
                        className="h-8 w-8"
                      >
                        <Icon name="FileText" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditClient(client)}
                        className="h-8 w-8"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile Cards */}
      <div className="lg:hidden space-y-4">
        {clients?.map((client) => (
          <div key={client?.id} className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3 flex-1">
                  <Checkbox
                    checked={selectedClients?.includes(client?.id)}
                    onChange={() => onSelectClient(client?.id)}
                  />
                  <ClientAvatar
                    src={client?.avatar_url || client?.avatar}
                    name={client?.name}
                    size={48}
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground truncate">{client?.name}</h3>
                    <p className="text-sm text-muted-foreground truncate">{client?.location}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => toggleRowExpansion(client?.id)}
                  className="h-8 w-8 flex-shrink-0"
                >
                  <Icon 
                    name={expandedRows?.has(client?.id) ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                  />
                </Button>
              </div>

              <div className="flex items-center justify-between mb-3">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getLifecycleStageColor(client?.lifecycle_stage)}`}>
                  {client?.lifecycle_stage || 'N/A'}
                </span>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${getEngagementColor(client?.engagement_level) === 'text-success' ? 'bg-success' : getEngagementColor(client?.engagement_level) === 'text-warning' ? 'bg-warning' : 'bg-error'}`} />
                  <span className={`text-sm font-medium ${getEngagementColor(client?.engagement_level)}`}>
                    {client?.engagement_level || 'N/A'}
                  </span>
                </div>
              </div>

              {expandedRows?.has(client?.id) && (
                <div className="space-y-3 pt-3 border-t border-border">
                  <div>
                    <p className="text-sm text-foreground">Phone: {client?.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">Last Contact: {client?.last_interaction ? new Date(client.last_interaction).toLocaleDateString() : 'Never'}</p>
                    <p className="text-xs text-muted-foreground">{client?.last_interaction_type || ''}</p>
                  </div>
                  {client?.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {client?.tags?.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-muted text-muted-foreground">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMessageClient(client)}
                  iconName="MessageSquare"
                  iconPosition="left"
                >
                  Message
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddNote(client)}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Note
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditClient(client)}
                  iconName="Edit"
                  iconPosition="left"
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default ClientTable;